import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import Editor from '../Editor';
import { ReadOnlyDocument } from './ReadOnlyDocument';
import { CommentOnlyDocument } from './CommentOnlyDocument';
import { CommenterVersionComparison } from './CommenterVersionComparison';
import { useLocale } from '../contexts/LocaleContext';
import { DocumentLoadingPanel } from './DocumentLoadingPanel';
import { CreateEntityFromMentionProvider } from '../contexts/CreateEntityFromMentionContext';
import { CreateEntityFromMentionHost } from './CreateEntityFromMentionHost';
import { OpenEntityDetailProvider } from '../contexts/OpenEntityDetailContext';
import { OpenEntityDetailHost } from './OpenEntityDetailHost';
import { MentionClickHandler } from './MentionClickHandler';

export const DocumentViewer: React.FC = () => {
  const { projectId, documentId, panel } = useParams<{
    projectId?: string;
    documentId: string;
    panel?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user, loading: authLoading } = useAuth();
  const { t } = useLocale();
  const [sharePermission, setSharePermission] = useState<'reader' | 'commenter' | 'editor' | null>(null);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const shareToken = searchParams.get('shareToken');
  const isVersionHistoryPanel = panel === 'version-history';

  useEffect(() => {
    const checkPermission = async (): Promise<void> => {
      if (authLoading) {
        return;
      }

      if (shareToken) {
        setIsLoading(false);
        return;
      }

      if (!documentId || documentId === 'new') {
        setIsLoading(false);
        return;
      }

      if (!token) {
        setError(t('no_permission') || "You don't have permission to access this page.");
        setIsLoading(false);
        return;
      }

      if (projectId && projectId !== 'new') {
        try {
          const projectResponse = await fetch(`${API_BASE}/api/projects/${projectId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (projectResponse.status === 401 || projectResponse.status === 403) {
            setError(t('no_permission') || "You don't have permission to access this page.");
            setIsLoading(false);
            return;
          }
        } catch (err) {
          console.error('Error checking project access:', err);
        }
      }

      try {
        const documentUrl = projectId && projectId !== 'new'
          ? `${API_BASE}/api/documents/${documentId}?contextProjectId=${encodeURIComponent(projectId)}`
          : `${API_BASE}/api/documents/${documentId}`;
        const response = await fetch(documentUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401 || response.status === 403) {
          setError(t('no_permission') || "You don't have permission to access this page.");
          setIsLoading(false);
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setSharePermission(data.sharePermission || null);
          setIsOwner(data.ownerId === user?.id);
          setError(null);
        } else {
          setError(t('failed_to_load_document') || 'Failed to load document');
        }
      } catch (err) {
        console.error('Error checking document permission:', err);
        setError(t('failed_to_load_document') || 'Failed to load document');
      } finally {
        setIsLoading(false);
      }
    };

    checkPermission();
  }, [token, documentId, projectId, user, shareToken, t, authLoading]);

  const isAuthenticatedProjectDocument =
    !authLoading &&
    !!token &&
    !!documentId &&
    documentId !== 'new' &&
    !!projectId &&
    !shareToken;

  const shouldMountEditorDuringPermissionCheck =
    isAuthenticatedProjectDocument && isLoading && !error;

  if (authLoading || (isLoading && !shouldMountEditorDuringPermissionCheck)) {
    return <DocumentLoadingPanel />;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '1rem' }}>{error}</div>
        <button
          onClick={() => navigate('/desk')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--scribane-black)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          {t('back_to_desk') || 'Back to Desk'}
        </button>
      </div>
    );
  }

  if (
    isOwner ||
    sharePermission === 'editor' ||
    sharePermission === 'reader' ||
    shouldMountEditorDuringPermissionCheck
  ) {
    if (projectId) {
      return <Editor />;
    }
    return (
      <CreateEntityFromMentionProvider>
        <OpenEntityDetailProvider>
          <Editor />
          <MentionClickHandler />
          <OpenEntityDetailHost />
          <CreateEntityFromMentionHost />
        </OpenEntityDetailProvider>
      </CreateEntityFromMentionProvider>
    );
  }

  if (sharePermission === 'commenter') {
    if (isVersionHistoryPanel && documentId && documentId !== 'new') {
      return <CommenterVersionComparison />;
    }
    return (
      <OpenEntityDetailProvider>
        <CommentOnlyDocument />
        <MentionClickHandler />
        <OpenEntityDetailHost />
      </OpenEntityDetailProvider>
    );
  }

  if (shareToken) {
    return <ReadOnlyDocument />;
  }

  console.error('DocumentViewer: document loaded without owner or share permission', {
    documentId,
    projectId,
    isOwner,
    sharePermission,
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
      <div style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '1rem' }}>
        {t('no_permission') || "You don't have permission to access this page."}
      </div>
      <button
        onClick={() => navigate('/desk')}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: 'var(--scribane-black)',
          color: 'white',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          fontSize: '1rem',
        }}
      >
        {t('back_to_desk') || 'Back to Desk'}
      </button>
    </div>
  );
};
